import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDebouncedCallback } from "use-debounce";
import InputControl from "./InputControl";

/**
 * @function DebouncedSearch
 * @description A search input that triggers a callback after debouncing user input
 * @param {Object} props component props
 * @param {string} [props.value] initial value of the search input
 * @param {Function} [props.onChange] callback that will be called after debouncing user input
 * @param {number} [props.debounce=500] delay in milliseconds before the callback will be called
 * @param {string} [props.placeholder='Search...'] placeholder text to be displayed in the input
 * @returns {ReactElement} The debounced search component
 */
export const DebouncedSearch = ({
  value: initialValue = "",
  onChange,
  debounce = 800,
  placeholder = "Search",
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  // Debounced callback for onChange
  const debouncedOnChange = useDebouncedCallback((val) => {
    onChange?.(val);
  }, debounce);

  // Update value and trigger debounced callback
  const handleChange = (event) => {
    const newValue = event.target.value;
    setValue(newValue);
    debouncedOnChange(newValue);
  };

  // Clear input and trigger immediate callback
  const handleClear = () => {
    setValue("");
    debouncedOnChange(""); // Ensure clearing also triggers the callback
  };

  return (
    <InputControl
      type="table-search"
      size="small"
      name="search"
      id="search"
      autoComplete="off"
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onClear={handleClear}
      {...props}
    />
  );
};

DebouncedSearch.propTypes = {
  debounce: PropTypes.number,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
};
